import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase, getWeekRange } from '../services/supabase';
import { useHouseholdId } from '../contexts/AuthContext';
import type {
  FamilyMember,
  Task,
  TaskAssignment,
  Announcement,
  CalendarEvent,
  Countdown,
  Meal,
  GroceryItem,
  DisplaySettings,
  TaskType,
  ScheduleCategory,
  ScheduleTemplate,
  TaskList,
  ListItem,
  MemberAchievement,
  LevelThreshold,
  VisualScheduleItem,
  VisualScheduleProgress,
  GamificationStats,
} from '../types';

// Legacy type aliases for backwards compatibility
type Chore = Task;
type ChoreAssignment = TaskAssignment;

// ============================================
// HOUSEHOLD HOOK
// ============================================

export function useHousehold() {
  const householdId = useHouseholdId();
  return { householdId: householdId || '' };
}

// ============================================
// FAMILY MEMBERS HOOK
// ============================================

export function useFamilyMembers() {
  const { householdId } = useHousehold();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMembers = useCallback(async () => {
    const { data } = await supabase
      .from('family_members')
      .select('*')
      .eq('household_id', householdId)
      .order('sort_order');
    setMembers(data || []);
    setLoading(false);
  }, [householdId]);

  const addMember = useCallback(async (member: Partial<FamilyMember>) => {
    const { error } = await supabase
      .from('family_members')
      .insert({ ...member, household_id: householdId });
    if (!error) fetchMembers();
    return !error;
  }, [householdId, fetchMembers]);

  const updateMember = useCallback(async (id: string, updates: Partial<FamilyMember>) => {
    const { error } = await supabase.from('family_members').update(updates).eq('id', id);
    if (!error) fetchMembers();
    return !error;
  }, [fetchMembers]);

  const deleteMember = useCallback(async (id: string) => {
    const { error } = await supabase.from('family_members').delete().eq('id', id);
    if (!error) fetchMembers();
    return !error;
  }, [fetchMembers]);

  useEffect(() => {
    fetchMembers();
    const channel = supabase
      .channel('family-members')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'family_members' }, fetchMembers)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchMembers]);

  return { members, loading, addMember, updateMember, deleteMember, refetch: fetchMembers };
}

// ============================================
// LEGACY CHORES HOOK (uses tasks table)
// ============================================

export function useChores() {
  const { householdId } = useHousehold();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [assignments, setAssignments] = useState<TaskAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async () => {
    const { data } = await supabase
      .from('tasks')
      .select('*')
      .eq('household_id', householdId)
      .eq('is_active', true)
      .order('sort_order');
    setTasks(data || []);
  }, [householdId]);

  const fetchAssignments = useCallback(async (startDate?: string, endDate?: string) => {
    const { start, end } = startDate && endDate ? { start: startDate, end: endDate } : getWeekRange();
    const { data } = await supabase
      .from('task_assignments')
      .select('*, task:tasks(*), assignee:family_members!assigned_to(*)')
      .eq('household_id', householdId)
      .gte('due_date', start)
      .lte('due_date', end)
      .order('due_date');
    setAssignments(data || []);
    setLoading(false);
  }, [householdId]);

  const createTask = useCallback(async (task: Partial<Task>) => {
    const { error } = await supabase.from('tasks').insert({ ...task, household_id: householdId });
    if (!error) fetchTasks();
    return !error;
  }, [householdId, fetchTasks]);

  const updateTask = useCallback(async (id: string, updates: Partial<Task>) => {
    const { error } = await supabase.from('tasks').update(updates).eq('id', id);
    if (!error) fetchTasks();
    return !error;
  }, [fetchTasks]);

  const deleteTask = useCallback(async (id: string) => {
    const { error } = await supabase.from('tasks').update({ is_active: false }).eq('id', id);
    if (!error) fetchTasks();
    return !error;
  }, [fetchTasks]);

  const assignTask = useCallback(async (taskId: string, assignedTo: string, dueDate: string, options?: Partial<TaskAssignment>) => {
    const { error } = await supabase.from('task_assignments').insert({
      household_id: householdId,
      task_id: taskId,
      assigned_to: assignedTo,
      due_date: dueDate,
      ...options,
    });
    if (!error) fetchAssignments();
    return !error;
  }, [householdId, fetchAssignments]);

  const toggleComplete = useCallback(async (assignmentId: string, completedBy?: string) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    if (!assignment) return false;
    const isCompleting = !assignment.is_completed;
    const { error } = await supabase
      .from('task_assignments')
      .update({
        is_completed: isCompleting,
        completed_at: isCompleting ? new Date().toISOString() : null,
        completed_by: isCompleting ? completedBy : null,
      })
      .eq('id', assignmentId);
    if (!error) fetchAssignments();
    return !error;
  }, [assignments, fetchAssignments]);

  useEffect(() => {
    fetchTasks();
    fetchAssignments();
    const channel = supabase
      .channel('task-assignments')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_assignments' }, () => fetchAssignments())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchTasks, fetchAssignments]);

  return {
    // New terminology
    tasks,
    assignments,
    loading,
    createTask,
    updateTask,
    deleteTask,
    assignTask,
    toggleComplete,
    refetch: fetchAssignments,
    // Legacy aliases
    chores: tasks,
    createChore: createTask,
    updateChore: updateTask,
    deleteChore: deleteTask,
    assignChore: assignTask,
  };
}

// ============================================
// ANNOUNCEMENTS HOOK
// ============================================

import {
  getActiveAnnouncements,
  dismissAnnouncement as dismissAnnouncementService,
  subscribeToAnnouncements,
  type GetAnnouncementsOptions,
} from '../services/announcements';

export interface UseAnnouncementsOptions {
  showOnDashboard?: boolean;
  showOnScreensaver?: boolean;
  currentUserId?: string; // For filtering dismissed announcements
  maxItems?: number;
}

export function useAnnouncements(options: UseAnnouncementsOptions = {}) {
  const { householdId } = useHousehold();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  const {
    showOnDashboard,
    showOnScreensaver,
    currentUserId,
    maxItems = 10,
  } = options;

  const fetchAnnouncements = useCallback(async () => {
    if (!householdId) return;

    const fetchOptions: GetAnnouncementsOptions = {
      showOnDashboard,
      showOnScreensaver,
      includeDismissedFor: currentUserId,
      maxItems,
    };

    const data = await getActiveAnnouncements(householdId, fetchOptions);
    setAnnouncements(data);
    setLoading(false);
  }, [householdId, showOnDashboard, showOnScreensaver, currentUserId, maxItems]);

  const dismiss = useCallback(async (announcementId: string) => {
    if (!currentUserId) return false;
    const success = await dismissAnnouncementService(announcementId, currentUserId);
    if (success) {
      // Optimistic update - remove from local state
      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
    }
    return success;
  }, [currentUserId]);

  useEffect(() => {
    if (!householdId) return;

    fetchAnnouncements();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToAnnouncements(householdId, fetchAnnouncements);
    return unsubscribe;
  }, [householdId, fetchAnnouncements]);

  return {
    announcements,
    loading,
    dismiss,
    refetch: fetchAnnouncements,
  };
}

// ============================================
// CALENDAR HOOK
// ============================================

export function useCalendar() {
  const { householdId } = useHousehold();
  const [events, setEvents] = useState<CalendarEvent[]>([]);

  const fetchEvents = useCallback(async (startDate: Date, endDate: Date) => {
    const { data } = await supabase
      .from('calendar_events')
      .select('*, family_member:family_members(*)')
      .eq('household_id', householdId)
      .gte('start_time', startDate.toISOString())
      .lte('start_time', endDate.toISOString())
      .order('start_time');
    setEvents(data || []);
  }, [householdId]);

  const createEvent = useCallback(async (event: Partial<CalendarEvent>) => {
    const { error } = await supabase.from('calendar_events').insert({ ...event, household_id: householdId, source: 'local' });
    return !error;
  }, [householdId]);

  useEffect(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    const end = new Date(now);
    end.setMonth(end.getMonth() + 1);
    fetchEvents(start, end);
  }, [fetchEvents]);

  return { events, fetchEvents, createEvent };
}

// ============================================
// COUNTDOWNS HOOK
// ============================================

export function useCountdowns() {
  const { householdId } = useHousehold();
  const [countdowns, setCountdowns] = useState<Countdown[]>([]);

  const fetchCountdowns = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('countdowns')
      .select('*')
      .eq('household_id', householdId)
      .gte('target_date', today)
      .order('target_date');
    setCountdowns(data || []);
  }, [householdId]);

  const createCountdown = useCallback(async (countdown: Partial<Countdown>) => {
    const { error } = await supabase.from('countdowns').insert({ ...countdown, household_id: householdId });
    if (!error) fetchCountdowns();
    return !error;
  }, [householdId, fetchCountdowns]);

  useEffect(() => { fetchCountdowns(); }, [fetchCountdowns]);

  return { countdowns, createCountdown, refetch: fetchCountdowns };
}

// ============================================
// MEALS HOOK
// ============================================

export function useMeals() {
  const { householdId } = useHousehold();
  const [meals, setMeals] = useState<Meal[]>([]);

  const fetchMeals = useCallback(async (startDate: string, endDate: string) => {
    const { data } = await supabase
      .from('meals')
      .select('*, cook:family_members(*)')
      .eq('household_id', householdId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date');
    setMeals(data || []);
  }, [householdId]);

  const setMeal = useCallback(async (date: string, mealType: string, name: string, options?: Partial<Meal>) => {
    const { error } = await supabase.from('meals').upsert({
      household_id: householdId,
      date,
      meal_type: mealType,
      name,
      ...options,
    }, { onConflict: 'household_id,date,meal_type' });
    return !error;
  }, [householdId]);

  return { meals, fetchMeals, setMeal };
}

// ============================================
// GROCERY LIST HOOK
// ============================================

export function useGroceryList() {
  const { householdId } = useHousehold();
  const [items, setItems] = useState<GroceryItem[]>([]);

  const fetchItems = useCallback(async () => {
    const { data } = await supabase
      .from('grocery_items')
      .select('*')
      .eq('household_id', householdId)
      .order('is_checked')
      .order('category')
      .order('sort_order');
    setItems(data || []);
  }, [householdId]);

  const addItem = useCallback(async (name: string, category?: string, quantity?: string) => {
    const { error } = await supabase.from('grocery_items').insert({
      household_id: householdId,
      name,
      category: category || 'Other',
      quantity,
    });
    if (!error) fetchItems();
    return !error;
  }, [householdId, fetchItems]);

  const toggleItem = useCallback(async (id: string, checkedBy?: string) => {
    const item = items.find(i => i.id === id);
    if (!item) return false;
    const { error } = await supabase.from('grocery_items').update({
      is_checked: !item.is_checked,
      checked_by: !item.is_checked ? checkedBy : null,
    }).eq('id', id);
    if (!error) fetchItems();
    return !error;
  }, [items, fetchItems]);

  const clearChecked = useCallback(async () => {
    const { error } = await supabase.from('grocery_items').delete().eq('household_id', householdId).eq('is_checked', true);
    if (!error) fetchItems();
    return !error;
  }, [householdId, fetchItems]);

  useEffect(() => {
    fetchItems();
    const channel = supabase
      .channel('grocery-items')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'grocery_items' }, fetchItems)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchItems]);

  return { items, addItem, toggleItem, clearChecked, refetch: fetchItems };
}

// ============================================
// DISPLAY SETTINGS HOOK
// ============================================

export function useDisplaySettings() {
  const { householdId } = useHousehold();
  const [settings, setSettings] = useState<DisplaySettings | null>(null);

  const fetchSettings = useCallback(async () => {
    const { data } = await supabase
      .from('display_settings')
      .select('*')
      .eq('household_id', householdId)
      .single();
    setSettings(data);
  }, [householdId]);

  const updateSettings = useCallback(async (updates: Partial<DisplaySettings>) => {
    const { error } = await supabase.from('display_settings').update(updates).eq('household_id', householdId);
    if (!error) fetchSettings();
    return !error;
  }, [householdId, fetchSettings]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  return { settings, updateSettings, refetch: fetchSettings };
}

// ============================================
// WEATHER HOOK
// ============================================

export function useWeather(lat?: number, lon?: number) {
  const [weather, setWeather] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!lat || !lon) return;

    const fetchWeather = async () => {
      const apiKey = import.meta.env.VITE_OPENWEATHER_API_KEY;
      if (!apiKey) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `https://api.openweathermap.org/data/3.0/onecall?lat=${lat}&lon=${lon}&units=imperial&appid=${apiKey}`
        );
        if (response.ok) {
          const data = await response.json();
          setWeather(data);
        }
      } catch (err) {
        console.error('Weather fetch failed:', err);
      }
      setLoading(false);
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 15 * 60 * 1000); // Refresh every 15 min
    return () => clearInterval(interval);
  }, [lat, lon]);

  return { weather, loading };
}

// ============================================
// TASKS HOOK (Extended Chores with Visual Schedules)
// ============================================

import {
  getTasks,
  getAssignments,
  getTodaysAssignments,
  createTask,
  updateTask,
  deleteTask,
  createAssignment,
  completeAssignment,
  uncompleteAssignment,
  skipAssignment,
  startAssignment,
  deleteAssignment,
  subscribeToTasks,
  subscribeToAssignments,
  type TaskFilters,
  type AssignmentFilters,
  type CreateTaskData,
  type CreateAssignmentData,
  type XPResult,
} from '../services/tasks';

export interface UseTasksOptions {
  taskType?: TaskType | TaskType[];
  scheduleCategory?: ScheduleCategory;
  memberId?: string;
  listId?: string;
  autoFetch?: boolean;
}

export function useTasks(options: UseTasksOptions = {}) {
  const { householdId } = useHousehold();
  const [tasks, setTasks] = useState<Chore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    taskType,
    scheduleCategory,
    memberId,
    listId,
    autoFetch = true,
  } = options;

  const fetchTasks = useCallback(async () => {
    if (!householdId) return;
    setLoading(true);
    try {
      const filters: TaskFilters = {
        taskType,
        scheduleCategory,
        listId,
      };
      const data = await getTasks(householdId, filters);
      setTasks(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, [householdId, taskType, scheduleCategory, listId]);

  const create = useCallback(async (data: Omit<CreateTaskData, 'household_id'>) => {
    if (!householdId) return null;
    const result = await createTask({ ...data, household_id: householdId });
    if (result) fetchTasks();
    return result;
  }, [householdId, fetchTasks]);

  const update = useCallback(async (taskId: string, updates: Partial<Chore>) => {
    const result = await updateTask(taskId, updates);
    if (result) fetchTasks();
    return result;
  }, [fetchTasks]);

  const remove = useCallback(async (taskId: string) => {
    const success = await deleteTask(taskId);
    if (success) fetchTasks();
    return success;
  }, [fetchTasks]);

  // Filter tasks by member age if memberId is provided
  const filteredTasks = useMemo(() => {
    if (!memberId) return tasks;
    // In a real implementation, we'd get member age and filter
    // For now, return all tasks
    return tasks;
  }, [tasks, memberId]);

  useEffect(() => {
    if (!autoFetch || !householdId) return;
    fetchTasks();
    const unsubscribe = subscribeToTasks(householdId, fetchTasks);
    return unsubscribe;
  }, [autoFetch, householdId, fetchTasks]);

  return {
    tasks: filteredTasks,
    loading,
    error,
    create,
    update,
    remove,
    refetch: fetchTasks,
  };
}

// ============================================
// TASK ASSIGNMENTS HOOK
// ============================================

export interface UseAssignmentsOptions {
  startDate?: string;
  endDate?: string;
  memberId?: string;
  templateId?: string;
  isCompleted?: boolean;
  includeSkipped?: boolean;
  autoFetch?: boolean;
}

export function useAssignments(options: UseAssignmentsOptions = {}) {
  const { householdId } = useHousehold();
  const [assignments, setAssignments] = useState<ChoreAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    startDate,
    endDate,
    memberId,
    templateId,
    isCompleted,
    includeSkipped = false,
    autoFetch = true,
  } = options;

  const fetchAssignments = useCallback(async () => {
    if (!householdId) return;
    setLoading(true);
    try {
      const filters: AssignmentFilters = {
        startDate,
        endDate,
        assignedTo: memberId,
        templateId,
        isCompleted,
        includeSkipped,
      };
      const data = await getAssignments(householdId, filters);
      setAssignments(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch assignments');
    } finally {
      setLoading(false);
    }
  }, [householdId, startDate, endDate, memberId, templateId, isCompleted, includeSkipped]);

  const create = useCallback(async (data: Omit<CreateAssignmentData, 'household_id'>) => {
    if (!householdId) return null;
    const result = await createAssignment({ ...data, household_id: householdId });
    if (result) fetchAssignments();
    return result;
  }, [householdId, fetchAssignments]);

  const complete = useCallback(async (
    assignmentId: string,
    completedBy: string,
    options?: { addXp?: boolean; updateStreak?: boolean }
  ): Promise<{ assignment: ChoreAssignment | null; xpResult?: XPResult }> => {
    const result = await completeAssignment(assignmentId, completedBy, options);
    if (result.assignment) fetchAssignments();
    return result;
  }, [fetchAssignments]);

  const uncomplete = useCallback(async (assignmentId: string) => {
    const result = await uncompleteAssignment(assignmentId);
    if (result) fetchAssignments();
    return result;
  }, [fetchAssignments]);

  const skip = useCallback(async (assignmentId: string, reason?: string) => {
    const result = await skipAssignment(assignmentId, reason);
    if (result) fetchAssignments();
    return result;
  }, [fetchAssignments]);

  const start = useCallback(async (assignmentId: string) => {
    const result = await startAssignment(assignmentId);
    if (result) fetchAssignments();
    return result;
  }, [fetchAssignments]);

  const remove = useCallback(async (assignmentId: string) => {
    const success = await deleteAssignment(assignmentId);
    if (success) fetchAssignments();
    return success;
  }, [fetchAssignments]);

  useEffect(() => {
    if (!autoFetch || !householdId) return;
    fetchAssignments();
    const unsubscribe = subscribeToAssignments(householdId, fetchAssignments);
    return unsubscribe;
  }, [autoFetch, householdId, fetchAssignments]);

  return {
    assignments,
    loading,
    error,
    create,
    complete,
    uncomplete,
    skip,
    start,
    remove,
    refetch: fetchAssignments,
  };
}

// ============================================
// TODAY'S ASSIGNMENTS HOOK (for visual schedules)
// ============================================

export function useTodaysAssignments(memberId?: string) {
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  return useAssignments({
    startDate: today,
    endDate: today,
    memberId,
    includeSkipped: false,
  });
}

// ============================================
// VISUAL SCHEDULE HOOK
// ============================================

export interface UseVisualScheduleOptions {
  memberId: string;
  category?: ScheduleCategory;
}

export function useVisualSchedule(options: UseVisualScheduleOptions) {
  const { memberId, category } = options;
  const { householdId } = useHousehold();

  // Get today's assignments for this member
  const {
    assignments,
    loading: assignmentsLoading,
    complete,
    skip,
    start,
    refetch,
  } = useTodaysAssignments(memberId);

  // Filter by schedule category if provided
  const filteredAssignments = useMemo(() => {
    if (!category) return assignments;
    return assignments.filter(a => a.task?.schedule_category === category);
  }, [assignments, category]);

  // Transform to visual schedule items
  const scheduleItems: VisualScheduleItem[] = useMemo(() => {
    return filteredAssignments.map((assignment, index) => {
      // Determine status
      let status: 'pending' | 'current' | 'completed' | 'skipped' = 'pending';
      if (assignment.is_completed) {
        status = 'completed';
      } else if (assignment.skipped) {
        status = 'skipped';
      } else if (assignment.actual_start_time) {
        status = 'current';
      }

      return {
        id: assignment.id,
        assignment,
        task: assignment.task!,
        status,
        order: index,
        scheduledTime: assignment.scheduled_time,
        isOptional: false, // Would come from template_item if available
      };
    });
  }, [filteredAssignments]);

  // Calculate progress
  const progress: VisualScheduleProgress = useMemo(() => {
    const total = scheduleItems.length;
    const completed = scheduleItems.filter(i => i.status === 'completed').length;
    const skipped = scheduleItems.filter(i => i.status === 'skipped').length;
    const pending = total - completed - skipped;

    return {
      total,
      completed,
      skipped,
      pending,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [scheduleItems]);

  // Get current item (first non-completed, non-skipped)
  const currentItem = useMemo(() => {
    return scheduleItems.find(i => i.status === 'pending' || i.status === 'current') || null;
  }, [scheduleItems]);

  // Actions
  const completeItem = useCallback(async (itemId: string, completedBy: string) => {
    return complete(itemId, completedBy);
  }, [complete]);

  const skipItem = useCallback(async (itemId: string, reason?: string) => {
    return skip(itemId, reason);
  }, [skip]);

  const startItem = useCallback(async (itemId: string) => {
    return start(itemId);
  }, [start]);

  return {
    items: scheduleItems,
    progress,
    currentItem,
    loading: assignmentsLoading,
    completeItem,
    skipItem,
    startItem,
    refetch,
  };
}

// ============================================
// SCHEDULE TEMPLATES HOOK
// ============================================

import {
  getScheduleTemplates,
  getScheduleTemplate,
  createScheduleTemplate,
  updateScheduleTemplate,
  deleteScheduleTemplate,
  addTemplateItem,
  updateTemplateItem,
  removeTemplateItem,
  reorderTemplateItems,
  createAssignmentsFromTemplate,
  subscribeToScheduleTemplates,
} from '../services/tasks';

export interface UseScheduleTemplatesOptions {
  memberId?: string;
  autoFetch?: boolean;
}

export function useScheduleTemplates(options: UseScheduleTemplatesOptions = {}) {
  const { householdId } = useHousehold();
  const [templates, setTemplates] = useState<ScheduleTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const { memberId, autoFetch = true } = options;

  const fetchTemplates = useCallback(async () => {
    if (!householdId) return;
    setLoading(true);
    const data = await getScheduleTemplates(householdId, memberId);
    setTemplates(data);
    setLoading(false);
  }, [householdId, memberId]);

  const create = useCallback(async (data: Partial<ScheduleTemplate> & { name: string; schedule_category: string }) => {
    if (!householdId) return null;
    const result = await createScheduleTemplate({ ...data, household_id: householdId });
    if (result) fetchTemplates();
    return result;
  }, [householdId, fetchTemplates]);

  const update = useCallback(async (templateId: string, updates: Partial<ScheduleTemplate>) => {
    const result = await updateScheduleTemplate(templateId, updates);
    if (result) fetchTemplates();
    return result;
  }, [fetchTemplates]);

  const remove = useCallback(async (templateId: string) => {
    const success = await deleteScheduleTemplate(templateId);
    if (success) fetchTemplates();
    return success;
  }, [fetchTemplates]);

  const addItem = useCallback(async (templateId: string, taskId: string, options?: Parameters<typeof addTemplateItem>[2]) => {
    const result = await addTemplateItem(templateId, taskId, options);
    if (result) fetchTemplates();
    return result;
  }, [fetchTemplates]);

  const updateItem = useCallback(async (itemId: string, updates: Parameters<typeof updateTemplateItem>[1]) => {
    const result = await updateTemplateItem(itemId, updates);
    if (result) fetchTemplates();
    return result;
  }, [fetchTemplates]);

  const removeItem = useCallback(async (itemId: string) => {
    const success = await removeTemplateItem(itemId);
    if (success) fetchTemplates();
    return success;
  }, [fetchTemplates]);

  const reorderItems = useCallback(async (updates: Array<{ id: string; display_order: number }>) => {
    const success = await reorderTemplateItems(updates);
    if (success) fetchTemplates();
    return success;
  }, [fetchTemplates]);

  const generateAssignments = useCallback(async (templateId: string, date: string, assignedBy?: string) => {
    return createAssignmentsFromTemplate(templateId, date, assignedBy);
  }, []);

  useEffect(() => {
    if (!autoFetch || !householdId) return;
    fetchTemplates();
    const unsubscribe = subscribeToScheduleTemplates(householdId, fetchTemplates);
    return unsubscribe;
  }, [autoFetch, householdId, fetchTemplates]);

  return {
    templates,
    loading,
    create,
    update,
    remove,
    addItem,
    updateItem,
    removeItem,
    reorderItems,
    generateAssignments,
    refetch: fetchTemplates,
  };
}

// ============================================
// TASK LISTS HOOK
// ============================================

import {
  getTaskLists,
  getTaskListWithTasks,
  createTaskList,
  updateTaskList,
  deleteTaskList,
  getListItems,
  createListItem,
  updateListItem,
  checkListItem,
  deleteListItem,
  clearCheckedItems,
  subscribeToListItems,
  type CreateListItemData,
} from '../services/tasks';

export interface UseTaskListsOptions {
  includeArchived?: boolean;
  listType?: string;
  autoFetch?: boolean;
}

export function useTaskLists(options: UseTaskListsOptions = {}) {
  const { householdId } = useHousehold();
  const [lists, setLists] = useState<TaskList[]>([]);
  const [loading, setLoading] = useState(true);

  const { includeArchived = false, listType, autoFetch = true } = options;

  const fetchLists = useCallback(async () => {
    if (!householdId) return;
    setLoading(true);
    const data = await getTaskLists(householdId, { includeArchived, listType });
    setLists(data);
    setLoading(false);
  }, [householdId, includeArchived, listType]);

  const create = useCallback(async (data: Partial<TaskList> & { name: string }) => {
    if (!householdId) return null;
    const result = await createTaskList({ ...data, household_id: householdId });
    if (result) fetchLists();
    return result;
  }, [householdId, fetchLists]);

  const update = useCallback(async (listId: string, updates: Partial<TaskList>) => {
    const result = await updateTaskList(listId, updates);
    if (result) fetchLists();
    return result;
  }, [fetchLists]);

  const remove = useCallback(async (listId: string, deleteTasks: boolean = false) => {
    const success = await deleteTaskList(listId, deleteTasks);
    if (success) fetchLists();
    return success;
  }, [fetchLists]);

  const getWithTasks = useCallback(async (listId: string) => {
    return getTaskListWithTasks(listId);
  }, []);

  useEffect(() => {
    if (!autoFetch || !householdId) return;
    fetchLists();
  }, [autoFetch, householdId, fetchLists]);

  return {
    lists,
    loading,
    create,
    update,
    remove,
    getWithTasks,
    refetch: fetchLists,
  };
}

// ============================================
// LIST ITEMS HOOK
// ============================================

export interface UseListItemsOptions {
  listId: string;
  autoFetch?: boolean;
}

export function useListItems(options: UseListItemsOptions) {
  const { householdId } = useHousehold();
  const { listId, autoFetch = true } = options;

  const [items, setItems] = useState<ListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  // Track when we're doing an optimistic update to skip subscription refetch
  const isOptimisticUpdateRef = useRef(false);

  const fetchItems = useCallback(async (showLoading = false) => {
    if (!listId) return;
    // Skip if this is triggered by subscription during our own optimistic update
    if (isOptimisticUpdateRef.current) {
      return;
    }
    // Only show loading on initial fetch, not background refetches
    if (showLoading || !hasFetchedRef.current) {
      setLoading(true);
    }
    try {
      const data = await getListItems(listId);
      setItems(data);
      setError(null);
      hasFetchedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch items');
    } finally {
      setLoading(false);
    }
  }, [listId]);

  // Optimistic add - show item immediately, then sync with server
  const addItem = useCallback(async (name: string, category?: string) => {
    if (!householdId || !listId) return null;

    // Block subscription refetch during optimistic update
    isOptimisticUpdateRef.current = true;

    // Optimistic: add temporary item to UI immediately
    const tempId = `temp-${Date.now()}`;
    const tempItem: ListItem = {
      id: tempId,
      list_id: listId,
      household_id: householdId,
      name,
      category,
      is_checked: false,
      priority: 0,
      sort_order: items.length,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setItems(prev => [...prev, tempItem]);

    // Server call in background
    const data: CreateListItemData = {
      list_id: listId,
      household_id: householdId,
      name,
      category,
    };
    const result = await createListItem(data);

    if (result) {
      // Replace temp item with real one
      setItems(prev => prev.map(item => item.id === tempId ? result : item));
    } else {
      // Revert on failure
      setItems(prev => prev.filter(item => item.id !== tempId));
    }

    // Re-enable subscription refetch after a short delay
    setTimeout(() => { isOptimisticUpdateRef.current = false; }, 500);
    return result;
  }, [householdId, listId, items.length]);

  // Optimistic check - toggle immediately, sync in background
  const check = useCallback(async (itemId: string, isChecked: boolean, checkedBy?: string) => {
    // Block subscription refetch during optimistic update
    isOptimisticUpdateRef.current = true;

    // Optimistic update
    setItems(prev => prev.map(item =>
      item.id === itemId
        ? { ...item, is_checked: isChecked, checked_by: checkedBy, checked_at: isChecked ? new Date().toISOString() : undefined }
        : item
    ));

    // Server call in background
    const result = await checkListItem(itemId, isChecked, checkedBy);
    if (!result) {
      // Revert on failure
      setItems(prev => prev.map(item =>
        item.id === itemId
          ? { ...item, is_checked: !isChecked, checked_by: undefined, checked_at: undefined }
          : item
      ));
    }

    // Re-enable subscription refetch after a short delay
    setTimeout(() => { isOptimisticUpdateRef.current = false; }, 500);
    return result;
  }, []);

  // Optimistic update - apply changes immediately
  const update = useCallback(async (itemId: string, updates: Partial<ListItem>) => {
    // Block subscription refetch during optimistic update
    isOptimisticUpdateRef.current = true;

    // Store previous state for rollback
    const previousItems = items;

    // Optimistic update
    setItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, ...updates } : item
    ));

    // Server call in background
    const result = await updateListItem(itemId, updates);
    if (!result) {
      // Revert on failure
      setItems(previousItems);
    }

    // Re-enable subscription refetch after a short delay
    setTimeout(() => { isOptimisticUpdateRef.current = false; }, 500);
    return result;
  }, [items]);

  // Optimistic remove - hide item immediately
  const remove = useCallback(async (itemId: string) => {
    // Block subscription refetch during optimistic update
    isOptimisticUpdateRef.current = true;

    // Store item for potential rollback
    const removedItem = items.find(item => item.id === itemId);

    // Optimistic remove
    setItems(prev => prev.filter(item => item.id !== itemId));

    // Server call in background
    const success = await deleteListItem(itemId);
    if (!success && removedItem) {
      // Revert on failure
      setItems(prev => [...prev, removedItem]);
    }

    // Re-enable subscription refetch after a short delay
    setTimeout(() => { isOptimisticUpdateRef.current = false; }, 500);
    return success;
  }, [items]);

  // Optimistic clear checked - remove checked items immediately
  const clearChecked = useCallback(async () => {
    // Block subscription refetch during optimistic update
    isOptimisticUpdateRef.current = true;

    // Store checked items for potential rollback
    const checkedItems = items.filter(item => item.is_checked);

    // Optimistic remove
    setItems(prev => prev.filter(item => !item.is_checked));

    // Server call in background
    const success = await clearCheckedItems(listId);
    if (!success) {
      // Revert on failure
      setItems(prev => [...prev, ...checkedItems]);
    }

    // Re-enable subscription refetch after a short delay
    setTimeout(() => { isOptimisticUpdateRef.current = false; }, 500);
    return success;
  }, [listId, items]);

  useEffect(() => {
    if (!autoFetch || !listId) return;
    fetchItems();
    const unsubscribe = subscribeToListItems(listId, fetchItems);
    return unsubscribe;
  }, [autoFetch, listId, fetchItems]);

  return {
    items,
    loading,
    error,
    addItem,
    update,
    check,
    remove,
    clearChecked,
    refetch: fetchItems,
  };
}

// ============================================
// GAMIFICATION HOOK
// ============================================

import {
  addXP,
  getLevelInfo,
  getLevelThresholds,
  getAchievements,
  getMemberAchievements,
  awardAchievement,
  checkAchievements,
  getStreakHistory,
  updateStreak,
  getGamificationStats,
  subscribeToAchievements,
} from '../services/tasks';

export interface UseGamificationOptions {
  memberId: string;
  autoFetch?: boolean;
}

export function useGamification(options: UseGamificationOptions) {
  const { memberId, autoFetch = true } = options;

  const [stats, setStats] = useState<GamificationStats | null>(null);
  const [levelThresholds, setLevelThresholds] = useState<LevelThreshold[]>([]);
  const [achievements, setAchievements] = useState<MemberAchievement[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const data = await getGamificationStats(memberId);

    // Calculate XP to next level
    const xpToNext = data.levelInfo.next
      ? data.levelInfo.next.xp_required - (data.member?.total_xp || 0)
      : 0;

    setStats({
      level: data.member?.level || 1,
      levelName: data.levelInfo.current?.name || 'Beginner',
      levelIcon: data.levelInfo.current?.icon || '🌱',
      totalXp: data.member?.total_xp || 0,
      xpToNextLevel: xpToNext,
      xpProgress: data.levelInfo.progress,
      currentStreak: data.member?.current_streak || 0,
      longestStreak: data.member?.longest_streak || 0,
      points: data.member?.points || 0,
      recentAchievements: data.achievements.slice(0, 5),
      streakFreezeAvailable: data.member?.streak_freeze_available ?? true,
    });
    setAchievements(data.achievements);
    setLoading(false);
  }, [memberId]);

  const fetchThresholds = useCallback(async () => {
    const data = await getLevelThresholds();
    setLevelThresholds(data);
  }, []);

  const earnXp = useCallback(async (xpAmount: number) => {
    const result = await addXP(memberId, xpAmount);
    if (result) {
      fetchStats();
      // Check for newly unlocked achievements
      if (stats) {
        await checkAchievements(memberId, {
          totalXP: result.new_total_xp,
          level: result.new_level,
        });
      }
    }
    return result;
  }, [memberId, fetchStats, stats]);

  const recordStreak = useCallback(async (date: string, wasPerfect: boolean) => {
    const result = await updateStreak(memberId, date, wasPerfect);
    if (result) {
      fetchStats();
      // Check for streak achievements
      await checkAchievements(memberId, {
        currentStreak: result.new_streak,
      });
    }
    return result;
  }, [memberId, fetchStats]);

  const earnAchievement = useCallback(async (achievementId: string) => {
    const result = await awardAchievement(memberId, achievementId);
    if (result) fetchStats();
    return result;
  }, [memberId, fetchStats]);

  useEffect(() => {
    if (!autoFetch) return;
    fetchStats();
    fetchThresholds();
    const unsubscribe = subscribeToAchievements(memberId, fetchStats);
    return unsubscribe;
  }, [autoFetch, memberId, fetchStats, fetchThresholds]);

  return {
    stats,
    levelThresholds,
    achievements,
    loading,
    earnXp,
    recordStreak,
    earnAchievement,
    refetch: fetchStats,
  };
}

// ============================================
// MEMBER GAMIFICATION SUMMARY HOOK
// ============================================

export function useMemberGamificationSummary(memberId: string) {
  const { stats, achievements, loading } = useGamification({ memberId });

  // Compute summary data
  const summary = useMemo(() => {
    if (!stats) return null;
    return {
      level: stats.level,
      levelName: stats.levelName,
      levelIcon: stats.levelIcon,
      totalXp: stats.totalXp,
      xpToNextLevel: stats.xpToNextLevel,
      progressPercent: stats.xpProgress,
      currentStreak: stats.currentStreak,
      longestStreak: stats.longestStreak,
      achievementCount: achievements.length,
      recentAchievements: stats.recentAchievements,
      streakFreezeAvailable: stats.streakFreezeAvailable,
    };
  }, [stats, achievements]);

  return { summary, loading };
}

// ============================================
// RE-EXPORT THEME HOOK
// ============================================

export { useTheme, type ThemeMode, type ResolvedTheme } from './useTheme';
